import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import axios from 'axios';
import { API_BASE } from '../config';
import Loading from './Loading';
import jsPDF from 'jspdf';
import { toPng } from 'html-to-image';
import RawMaterialFlowDiagram from './RawMaterialFlowDiagram';
import { useLanguage } from '../LanguageContext';
import translations from '../translations';

const RawMaterialDetail = () => {
  const { t, isRtl } = useLanguage();
  const { uuid, workspaceUuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [rawMaterial, setRawMaterial] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const diagramRef = React.useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_BASE}/rawmaterials`, { headers: getAuthHeaders() });
        const found = (res.data || []).find(r => r.uuid === uuid);
        if (!found) {
          alert(t.no_raw_found);
          navigate(`/workspace/${workspaceUuid}/raw-materials`);
          return;
        }
        setRawMaterial(found);

        // fetch products if product_ids exist
        if (found.product_ids && found.product_ids.length > 0) {
          setProductsLoading(true);
          try {
            const pRes = await axios.get(`${API_BASE}/products`, { headers: getAuthHeaders() });
            const relatedProducts = (pRes.data || []).filter(p => found.product_ids.includes(p._id));
            setProducts(relatedProducts);
          } catch (err) {
            console.error('Failed to fetch products:', err);
          } finally {
            setProductsLoading(false);
          }
        }
      } catch (err) {
        console.error(err);
        alert(t.fetch_failed);
        navigate(`/workspace/${workspaceUuid}/raw-materials`);
      } finally {
        setLoading(false);
      }
    };
    setLoading(true);
    setRawMaterial(null);
    fetch();
  }, [uuid]);


  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('autoOpenOverview') === 'true') {
      setOverviewOpen(true);
      // Clean up URL
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);


  const handleExportPDF = async () => {
    if (!rawMaterial) return;
    setExportLoading(true);
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pdfT = translations.english;
      const pdfIsRtl = false;
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;
      let y = 20;

      // --- Title ---
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(22);
      doc.text(String(rawMaterial.material_name), pdfIsRtl ? pageWidth - margin : margin, y, { align: pdfIsRtl ? 'right' : 'left' });
      y += 8;

      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100);
      doc.text(`${pdfT.material_code}: #${rawMaterial.material_code}`, pdfIsRtl ? pageWidth - margin : margin, y, { align: pdfIsRtl ? 'right' : 'left' });
      y += 12;

      // --- General Info ---
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(0);
      doc.text(String(pdfT.general_information || ""), pdfIsRtl ? pageWidth - margin : margin, y, { align: pdfIsRtl ? 'right' : 'left' });
      y += 8;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const info = [
        [`${pdfT.batch_number}:`, rawMaterial.batch_number || '-'],
        [`${pdfT.supplier}:`, rawMaterial.supplier_name || '-'],
        [`${pdfT.actual_quantity}:`, `${rawMaterial.actual_quantity || 0} ${pdfT.kg}`],
        [`${pdfT.available_quantity}:`, `${rawMaterial.available_quantity || 0} ${pdfT.kg}`],
        [`${pdfT.manufacture_date}:`, formatDate(rawMaterial.manufacturing_date)],
        [`${pdfT.receiving_date}:`, formatDate(rawMaterial.receiving_date)],
        [`${pdfT.export_date}:`, new Date().toLocaleDateString(pdfIsRtl ? 'ar-EG' : 'en-US')]
      ];

      info.forEach(([label, value]) => {
        doc.setFont('helvetica', 'bold');
        doc.text(String(label || ""), pdfIsRtl ? pageWidth - margin : margin, y, { align: pdfIsRtl ? 'right' : 'left' });
        doc.setFont('helvetica', 'normal');
        doc.text(String(value || ""), pdfIsRtl ? pageWidth - margin - 50 : margin + 50, y, { align: pdfIsRtl ? 'right' : 'left' });
        y += 6;
      });
      y += 10;

      // --- Products Using This Material ---
      if (products.length > 0) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text(String(pdfT.products_using_this_material || ""), pdfIsRtl ? pageWidth - margin : margin, y, { align: pdfIsRtl ? 'right' : 'left' });
        y += 8;

        const headers = [pdfT.product, pdfT.batch, pdfT.code, pdfT.qty_used, pdfT.manufacture_date];
        const data = products.map(p => {
          const mId = rawMaterial._id || rawMaterial.id;
          const usage = (p.used_raw_materials || []).find(urm => {
            const urmId = urm.rawMaterialId?._id || urm.rawMaterialId;
            return String(urmId) === String(mId);
          });
          return [
            p.product_name,
            p.batch || '-',
            p.product_code,
            `${usage?.quantity || 0} ${pdfT.kg}`,
            formatDate(p.manufacture_date)
          ];
        });

        let startX = margin;
        let cellWidth = (pageWidth - margin * 2) / 5;

        doc.setFontSize(10);
        headers.forEach((h, i) => {
          doc.setFillColor(240, 240, 240);
          doc.rect(startX + i * cellWidth, y - 5, cellWidth, 7, 'F');
          doc.text(String(h || ""), startX + i * cellWidth + 2, y);
        });
        y += 7;

        data.forEach(row => {
          if (y > 270) { doc.addPage(); y = 20; }
          row.forEach((cell, i) => {
            doc.text(String(cell), startX + i * cellWidth + 2, y);
          });
          y += 6;
        });
        y += 15;
      }

      // --- Page 2: Material Flow Diagram ---
      if (diagramRef.current) {
        // Wait for rendering transition/data
        await new Promise(r => setTimeout(r, 800));

        const captureWidth = 1400;
        const imgData = await toPng(diagramRef.current, {
          quality: 1.0,
          pixelRatio: 3,
          backgroundColor: '#fafbfc',
          width: captureWidth,
          skipFonts: true,
          style: {
            fontFamily: "'Outfit', -apple-system, system-ui, sans-serif",
            fontWeight: '400'
          }
        });

        doc.addPage();
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.text(String(pdfT.material_usage_diagram || ""), pdfIsRtl ? pageWidth - margin : margin, 20, { align: pdfIsRtl ? 'right' : 'left' });

        const originalHeight = diagramRef.current.offsetHeight;
        const originalWidth = diagramRef.current.offsetWidth;
        const aspectRatio = originalHeight / originalWidth;

        const pdfImgWidth = pageWidth - margin * 2;
        const pdfImgHeight = pdfImgWidth * aspectRatio;

        doc.addImage(imgData, 'PNG', margin, 35, pdfImgWidth, pdfImgHeight);
      }

      doc.save(`${rawMaterial.material_name}_Report.pdf`);
      setShowExportConfirm(false);
    } catch (err) {
      console.error('PDF Export Error:', err);
      alert(t.fetch_failed);
    } finally {
      setExportLoading(false);
    }
  };

  const isAutoOpening = new URLSearchParams(location.search).get('autoOpenOverview') === 'true';

  if (loading) return <Loading type="full" message={isAutoOpening ? t.preparing_overview : t.fetching_material_details} />;

  if (!rawMaterial) return null;

  const formatDate = d => d ? new Date(d).toLocaleDateString(isRtl ? 'ar-EG' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';

  return (
    <>
      <div className="fade-in" style={{ maxWidth: 1000, margin: '0 auto', paddingBottom: 60 }}>
        {/* Hidden container for PDF export capture */}
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', overflow: 'visible', direction: 'ltr' }}>
          <div ref={diagramRef} style={{ width: 1400, padding: 60, background: '#fafbfc', minHeight: 800 }}>
            <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 20, textAlign: 'center' }}>{translations.english.material_usage_diagram}</h2>
            <RawMaterialFlowDiagram rawMaterial={rawMaterial} products={products} currentUuid={rawMaterial?.uuid} forceT={translations.english} forceIsRtl={false} />

          </div>
        </div>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
            <button
              onClick={() => navigate(`/workspace/${workspaceUuid}/raw-materials`)}
              style={{
                background: 'none', border: '1px solid #d2d2d2', borderRadius: 6, padding: '6px 8px',
                cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#6d7175', transform: isRtl ? 'rotate(180deg)' : 'none'
              }}
            >
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1a1c1d', textAlign: isRtl ? 'right' : 'left' }}>
              {rawMaterial.material_name}
              <span style={{ color: '#6d7175', fontSize: 14, fontWeight: 500, [isRtl ? 'marginRight' : 'marginLeft']: 10 }}>#{rawMaterial.material_code}</span>
              <div style={{ display: 'inline-flex', gap: 12, [isRtl ? 'marginRight' : 'marginLeft']: 20, verticalAlign: 'middle', flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                <span style={{ fontSize: 13, background: '#e3f2fd', color: '#0d47a1', padding: '2px 10px', borderRadius: 6, fontWeight: 600, border: '1px solid #90caf9' }}>
                  {t.total}: {Number(rawMaterial.actual_quantity || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })} {t.kg}
                </span>
                <span style={{ fontSize: 13, background: '#eff6ff', color: '#1e40af', padding: '2px 10px', borderRadius: 6, fontWeight: 600, border: '1px solid #bfdbfe' }}>
                  {t.in_stock}: {Number(rawMaterial.available_quantity || 0).toLocaleString(isRtl ? 'ar-EG' : undefined, { maximumFractionDigits: 2 })} {t.kg}
                </span>
              </div>
            </h1>
          </div>
          <div style={{ display: 'flex', gap: 8, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
            <button
              onClick={() => setShowExportConfirm(true)}
              disabled={exportLoading}
              style={{
                padding: '8px 14px', borderRadius: 6, border: '1px solid #d2d2d2',
                background: '#fff', color: '#1a1c1d', fontWeight: 600, fontSize: 13,
                cursor: exportLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                opacity: exportLoading ? 0.7 : 1, minWidth: 105, justifyContent: 'center',
                flexDirection: isRtl ? 'row-reverse' : 'row'
              }}
            >
              {exportLoading ? (
                <div className="dots-loading">
                  <span style={{ backgroundColor: '#1976d2' }}></span>
                  <span style={{ backgroundColor: '#1976d2' }}></span>
                  <span style={{ backgroundColor: '#1976d2' }}></span>
                </div>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isRtl ? 'scaleX(-1)' : 'none' }}>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  {t.export_btn}
                </>
              )}
            </button>
            <button onClick={() => setOverviewOpen(true)} style={{
              padding: '8px 14px', borderRadius: 6, border: 'none',
              background: 'linear-gradient(135deg, #1976D2, #1565C0)', color: '#fff', fontWeight: 600, fontSize: 13,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              boxShadow: '0 2px 8px rgba(25,118,210,0.25)', transition: 'all 0.2s',
              flexDirection: isRtl ? 'row-reverse' : 'row'
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ transform: isRtl ? 'scaleX(-1)' : 'none' }}>
                <circle cx="12" cy="5" r="3" stroke="#fff" strokeWidth="1.5" />
                <circle cx="5" cy="19" r="3" stroke="#fff" strokeWidth="1.5" />
                <circle cx="19" cy="19" r="3" stroke="#fff" strokeWidth="1.5" />
                <line x1="12" y1="8" x2="5" y2="16" stroke="#fff" strokeWidth="1.5" />
                <line x1="12" y1="8" x2="19" y2="16" stroke="#fff" strokeWidth="1.5" />
              </svg>
              {t.see_overview}
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
          {/* General Information Card */}
          <section style={{
            background: '#fff', padding: 20, borderRadius: 12,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', border: '1px solid #ebebeb',
            direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: 16, color: '#1a1c1d', fontSize: 14, fontWeight: 600 }}>{t.general_information}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <div style={{ fontSize: 12, color: '#6d7175', marginBottom: 4 }}>{t.material_name}</div>
                <div style={{ fontWeight: 500, color: '#1a1c1d', fontSize: 14 }}>{rawMaterial.material_name}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6d7175', marginBottom: 4 }}>{t.material_code}</div>
                <div style={{ fontWeight: 500, color: '#1a1c1d', fontSize: 14 }}>{rawMaterial.material_code}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6d7175', marginBottom: 4 }}>{t.batch_number}</div>
                <div style={{ fontWeight: 500, color: '#1a1c1d', fontSize: 14 }}>{rawMaterial.batch_number}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6d7175', marginBottom: 4 }}>{t.supplier}</div>
                <div style={{ fontWeight: 500, color: '#1a1c1d', fontSize: 14 }}>{rawMaterial.supplier_name || '-'}</div>
              </div>
            </div>
          </section>

          {/* Inventory Status Card */}
          <section style={{
            background: '#fff', padding: 20, borderRadius: 12,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', border: '1px solid #ebebeb',
            direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: 16, color: '#1a1c1d', fontSize: 14, fontWeight: 600 }}>{t.inventory_status}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <div style={{ fontSize: 12, color: '#6d7175', marginBottom: 4 }}>{t.actual_quantity}</div>
                <div style={{ fontWeight: 600, color: '#1a1c1d', fontSize: 14, textAlign: isRtl ? 'right' : 'left' }}>{rawMaterial.actual_quantity ?? '-'} {t.kg}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6d7175', marginBottom: 4 }}>{t.available_quantity}</div>
                <div style={{
                  fontWeight: 600,
                  color: (rawMaterial.available_quantity || 0) <= 10 ? '#d82c0d' : '#1976d2',
                  fontSize: 14, textAlign: isRtl ? 'right' : 'left'
                }}>
                  {rawMaterial.available_quantity ?? '-'} {t.kg}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6d7175', marginBottom: 4 }}>{t.manufacturing_date}</div>
                <div style={{ fontWeight: 500, color: '#1a1c1d', fontSize: 14 }}>{formatDate(rawMaterial.manufacturing_date)}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6d7175', marginBottom: 4 }}>{t.receiving_date}</div>
                <div style={{ fontWeight: 500, color: '#1a1c1d', fontSize: 14 }}>{formatDate(rawMaterial.receiving_date)}</div>
              </div>
            </div>
          </section>
        </div>

        {/* Products Using This Raw Material */}
        <section className="fade-in" style={{
          background: '#fff', borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #ebebeb',
          overflow: 'hidden', direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left'
        }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f1f1' }}>
            <h3 style={{ margin: 0, color: '#1a1c1d', fontSize: 14, fontWeight: 600 }}>{t.products_using_this_material}</h3>
          </div>
          {productsLoading ? (
            <Loading message={t.loading_products} />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, color: '#1a1c1d' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '1px solid #ebebeb' }}>
                    <th style={{ width: 40, padding: '10px 0' }}></th>
                    <th style={{ padding: '10px 20px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{t.product}</th>
                    <th style={{ padding: '10px 20px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{t.batch}</th>
                    <th style={{ padding: '10px 20px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{t.code}</th>
                    <th style={{ padding: '10px 20px', fontWeight: 600, textAlign: isRtl ? 'left' : 'right' }}>{t.raw_material_qty_used}</th>
                    <th style={{ padding: '10px 20px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{t.manufacture_date}</th>
                  </tr>
                </thead>
                <tbody>
                  {products.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ padding: 40, textAlign: 'center', color: '#6d7175' }}>
                        {t.no_products_using_this_material}
                      </td>
                    </tr>
                  ) : (
                    products.map((p, i) => {
                      const mId = rawMaterial._id || rawMaterial.id;
                      const usage = (p.used_raw_materials || []).find(urm => {
                        const urmId = urm.rawMaterialId?._id || urm.rawMaterialId;
                        return String(urmId) === String(mId);
                      });
                      return (
                        <tr
                          key={i}
                          style={{ borderBottom: '1px solid #f1f1f1' }}
                          onMouseOver={e => e.currentTarget.style.background = '#f9fafb'}
                          onMouseOut={e => e.currentTarget.style.background = '#fff'}
                        >
                            <td style={{ textAlign: 'center', padding: '12px 0' }}>
                              <button
                                onClick={() => navigate(`/workspace/${workspaceUuid}/products/${p.uuid}`)}
                                style={{
                                  background: 'none', border: 'none', cursor: 'pointer',
                                  color: '#2c6ecb', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  margin: '0 auto', padding: 4, borderRadius: 4, transform: isRtl ? 'scaleX(-1)' : 'none'
                                }}
                                onMouseOver={e => e.currentTarget.style.background = '#eef4ff'}
                                onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                                title={t.view_product_details}
                              >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2 2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                              </button>
                            </td>
                            <td style={{ padding: '12px 20px', fontWeight: 600, textAlign: isRtl ? 'right' : 'left' }}>{p.product_name}</td>
                            <td style={{ padding: '12px 20px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{p.batch || '-'}</td>
                            <td style={{ padding: '12px 20px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{p.product_code}</td>
                            <td style={{ padding: '12px 20px', textAlign: isRtl ? 'left' : 'right', fontWeight: 600, color: '#1976d2' }}>
                              {Number(usage?.quantity || 0).toLocaleString(isRtl ? 'ar-EG' : undefined)} {t.kg}
                            </td>
                            <td style={{ padding: '12px 20px', color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>{formatDate(p.manufacture_date)}</td>
                          </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {/* Overview Modal - Outside fade-in for centering */}
      {overviewOpen && (
        <div className="overlay-fade-in" style={{
          position: 'fixed', inset: 0, background: 'rgba(26, 28, 29, 0.55)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1200,
          backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)'
        }}>
          <div className="modal-fade-in" style={{
            width: '100vw', height: '100vh', background: '#fff',
            boxShadow: '0 28px 60px rgba(0,0,0,0.25)', overflow: 'hidden',
            display: 'flex', flexDirection: 'column', position: 'relative',
            direction: isRtl ? 'rtl' : 'ltr'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '16px 24px', borderBottom: '1px solid #ebebeb',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              flexDirection: isRtl ? 'row-reverse' : 'row'
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1c1d', textAlign: isRtl ? 'right' : 'left' }}>{t.material_overview}</h2>
                <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>
                  {t.usage_flow_for} {rawMaterial.material_name}
                </p>
              </div>
              <button onClick={() => setOverviewOpen(false)} style={{
                background: 'none', border: 'none', cursor: 'pointer', color: '#6d7175', padding: 4
              }}>
                <svg width="24" height="24" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            {/* Modal Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
              <RawMaterialFlowDiagram rawMaterial={rawMaterial} products={products} currentUuid={rawMaterial?.uuid} />

            </div>
          </div>
        </div>
      )}

      {showExportConfirm && (
        <div className="overlay-fade-in" style={{
          position: 'fixed', inset: 0, background: 'rgba(26, 28, 29, 0.45)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1300,
          backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)'
        }}>
          <div className="modal-fade-in" style={{ width: 400, background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 20px 40px rgba(0,0,0,0.2)', direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: 18, fontWeight: 700, color: '#1a1c1d' }}>{t.export_material_report}</h3>
            <p style={{ margin: '0 0 24px 0', fontSize: 14, color: '#6d7175', lineHeight: 1.5 }}>
              {t.export_material_report_msg_long}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
              <button onClick={() => setShowExportConfirm(false)} disabled={exportLoading} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #d2d2d2', background: '#fff', color: '#1a1c1d', fontWeight: 600, fontSize: 13, cursor: exportLoading ? 'not-allowed' : 'pointer', opacity: exportLoading ? 0.6 : 1 }}>{t.cancel_btn}</button>
              <button onClick={handleExportPDF} disabled={exportLoading} style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#1976d2', color: '#fff', fontWeight: 600, fontSize: 13, cursor: exportLoading ? 'not-allowed' : 'pointer', boxShadow: '0 2px 4px rgba(25,118,210,0.2)', display: 'flex', alignItems: 'center', gap: 8, opacity: exportLoading ? 0.8 : 1, minWidth: 100, justifyContent: 'center', flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                {exportLoading ? <div className="dots-loading"><span></span><span></span><span></span></div> : t.start_export}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RawMaterialDetail;
